import { useCallback, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  TrainingFormsMembershipsDocument,
  TrainingKnowledgeFormMembershipsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { ExportCertificates } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ExportCertificate'
import {
  BulkSelection,
  useBulkAction,
} from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'
import { useColumns } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useColumns'
import { useDataSource } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useDataSource'
import { TrainingParticipantData } from 'modules/trainings/lib/TrainingParticipantData'
import {
  CompletionConfiguration,
  DetailOutletContext,
  TrainingsTraining,
  TrainingsTrainingClass,
} from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

const DEFAULT_SELECTION: BulkSelection = { type: 'manual', ids: [] }

const ParticipantsOneDataTable = ({
  training,
  trainingClasses,
  completionConfiguration,
  outletContext,
}: {
  training: TrainingsTraining
  trainingClasses: TrainingsTrainingClass[]
  completionConfiguration: CompletionConfiguration
  outletContext: DetailOutletContext
}) => {
  const { data: formData } = useQuery(TrainingFormsMembershipsDocument, {
    variables: { resourceId: String(training.id) },
    skip: !completionConfiguration?.knowledgeTestRequired,
    fetchPolicy: 'cache-and-network',
  })

  const knowledgeFormId = formData?.forms?.formsConnection?.edges?.[0]?.node?.id ?? ''

  const { data: participantsData } = useQuery(TrainingKnowledgeFormMembershipsDocument, {
    variables: {
      formId: knowledgeFormId,
    },
    skip: !completionConfiguration?.knowledgeTestRequired && !knowledgeFormId,
  })

  const trainingParticipantData = useMemo(
    () =>
      new TrainingParticipantData(
        training,
        trainingClasses ?? [],
        completionConfiguration,
        participantsData?.forms?.membershipsConnection?.nodes ?? null
      ),
    [training, trainingClasses, participantsData, completionConfiguration]
  )

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([])

  const allEmployeeIds = useMemo(
    () =>
      outletContext.members
        .map((membership) => membership.employee?.id)
        .filter((id): id is number => id !== undefined),
    [outletContext.members]
  )

  const openExportModal = useCallback(
    (selection: BulkSelection) => {
      let resolvedIds: number[]
      if (selection.type === 'all') {
        const excludedIds = new Set(selection.excludedIds)
        resolvedIds = allEmployeeIds.filter((id) => !excludedIds.has(id))
      } else {
        resolvedIds = selection.ids
      }
      setSelectedParticipantIds(resolvedIds)
      setExportModalOpen(true)
    },
    [allEmployeeIds]
  )

  const closeExportModal = useCallback(() => {
    setExportModalOpen(false)
  }, [])

  const source = useDataSource(training, trainingClasses, () => openExportModal(DEFAULT_SELECTION))
  const columns = useColumns(trainingParticipantData)
  const handleBulkAction = useBulkAction(trainingClasses, openExportModal)

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns,
            },
          },
        ]}
      />
      <ExportCertificates
        isOpen={exportModalOpen}
        onClose={closeExportModal}
        trainingMemberships={outletContext.members}
        selectedParticipantIds={selectedParticipantIds}
      />
      <Outlet context={outletContext} />
    </>
  )
}

export default ParticipantsOneDataTable
